import { OrganizationUserRole } from 'libs/end-to-end/src/types/users/organization-user-roles';
import { SSORoleMapping } from 'libs/end-to-end/src/sso';
import { SSORoleMappingComparisonType } from 'libs/end-to-end/src/sso';
import { mapOverEnum } from 'libs/end-to-end/src/arrays';
import React, { useMemo, useState } from 'react';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { LegacyButton } from 'apps/sigma2-ui/src/components/ui/LegacyButton';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { TextInput } from 'apps/sigma2-ui/src/components/ui/TextInput';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { useAsyncEffect } from 'apps/sigma2-ui/src/lib/hooks/useAsyncEffect';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { Checkbox } from '@mui/material';

// TODO: The complexity of this code is greater than 10. Refer to https://eslint.org/docs/latest/rules/complexity
export const AdminSSORoleMappingPage = () => {
  const [searchParams] = useSearchParams();

  const routeS2ClaimName = searchParams.get('s2ClaimName') ?? null;
  const routePropertyValue = searchParams.get('propertyValue') ?? null;
  const rawComparisonType = searchParams.get('comparisonType') ?? null;
  const parsedResult =
    SSORoleMappingComparisonType.safeParse(rawComparisonType);

  const { identityProviderId, mappingType: routeClaimMappingType } =
    useParams();

  let routeComparisonType: SSORoleMappingComparisonType | null = null;

  const isNewClaimMapping = routeClaimMappingType === 'new';
  if (!isNewClaimMapping && !parsedResult.success) {
    throw new Error('Invalid route parameters');
  } else if (parsedResult.success) {
    routeComparisonType = parsedResult.data;
  } else {
    routeComparisonType = null;
  }

  const { myUser, userAuthenticatedApiCall } = useUser();
  const effectiveRouteS2ClaimName = routeS2ClaimName ?? '';
  const effectiveRouteComparisonType = routeComparisonType ?? 'equals';
  const effectiveRoutePropertyValue = routePropertyValue ?? '';

  if (
    !isNewClaimMapping &&
    (!routeS2ClaimName || !routePropertyValue || !routeComparisonType)
  ) {
    throw new Error('Invalid route parameters');
  }

  const [isLoadingClaimMapping, setIsLoadingClaimMapping] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [s2ClaimName, setS2ClaimName] = useState<string>(
    effectiveRouteS2ClaimName,
  );
  const [comparisonType, setComparisonType] =
    useState<SSORoleMappingComparisonType>(effectiveRouteComparisonType);
  const [propertyValue, setPropertyValue] = useState<string>(
    effectiveRoutePropertyValue,
  );
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const [organizationRole, setOrganizationRole] =
    useState<OrganizationUserRole | null>(null);

  const [isUpdatingClaimMapping, setIsUpdatingClaimMapping] = useState(false);
  const [isCreatingClaimMapping, setIsCreatingClaimMapping] = useState(false);
  const [mostRecentError, setMostRecentError] = useState<string | null>(null);

  const navigate = useNavigateSafe();

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (isAlive) => {
      if (
        !isAlive() ||
        !identityProviderId ||
        !myUser ||
        myUser.roleAssignments !== 'superAdmin' ||
        isNewClaimMapping ||
        hasLoadedOnce
      ) {
        return;
      }

      setIsLoadingClaimMapping(true);
      setHasLoadedOnce(true);

      try {
        const getResult = await userAuthenticatedApiCall({
          responseSchema: SSORoleMapping,
          url: `${getApiBaseUrl()}/admin/sso/identity-providers/${identityProviderId}/role-mappings/role-mapping?s2ClaimName=${encodeURIComponent(
            effectiveRouteS2ClaimName,
          )}&propertyValue=${encodeURIComponent(
            effectiveRoutePropertyValue,
          )}&comparisonType=${encodeURIComponent(
            effectiveRouteComparisonType,
          )}`,
          requestOptions: {
            method: 'GET',
          },
        });

        if (!isAlive() || !getResult) {
          return;
        }

        if (getResult.resultType === 'err') {
          return;
        }

        setS2ClaimName(getResult.value.s2ClaimName);
        setComparisonType(getResult.value.comparisonType);
        setPropertyValue(getResult.value.propertyValue);
        setIsSuperAdmin(getResult.value.isSuperAdmin);
        setOrganizationRole(getResult.value.organizationRole ?? null);
        setIsLoadingClaimMapping(false);
      } catch (e) {
        setIsLoadingClaimMapping(false);
        return;
      }
    },
    [
      myUser,
      hasLoadedOnce,
      isNewClaimMapping,
      userAuthenticatedApiCall,
      identityProviderId,
      effectiveRouteS2ClaimName,
      effectiveRoutePropertyValue,
      effectiveRouteComparisonType,
    ],
  );

  const updateClaimMapping = useAsyncCallback(
    async (isAlive) => {
      if (
        !isAlive() ||
        isNewClaimMapping ||
        isLoadingClaimMapping ||
        isUpdatingClaimMapping
      ) {
        return;
      }

      setIsUpdatingClaimMapping(true);

      try {
        const requestClaimMapping: SSORoleMapping = {
          s2ClaimName,
          comparisonType,
          propertyValue,
          organizationRole: organizationRole ?? undefined,
          isSuperAdmin,
        };

        const updateResult = await userAuthenticatedApiCall({
          responseSchema: SSORoleMapping,
          url: `${getApiBaseUrl()}/admin/sso/identity-providers/${identityProviderId}/role-mappings/role-mapping?s2ClaimName=${encodeURIComponent(
            effectiveRouteS2ClaimName,
          )}&propertyValue=${encodeURIComponent(
            effectiveRoutePropertyValue,
          )}&comparisonType=${encodeURIComponent(
            effectiveRouteComparisonType,
          )}`,
          requestOptions: {
            method: 'PUT',
            body: JSON.stringify(requestClaimMapping),
          },
        });

        if (!isAlive() || !updateResult) {
          return;
        }

        if (updateResult.resultType === 'err') {
          // TODO: FIXME
          setMostRecentError(JSON.stringify(updateResult.error));
          return;
        }

        navigate(
          `/sso-main/identity-provider/${identityProviderId}/role-mappings`,
        );
      } catch (e) {
        if (isAlive()) {
          setIsUpdatingClaimMapping(false);
        }
      }
    },
    [
      isNewClaimMapping,
      isLoadingClaimMapping,
      isUpdatingClaimMapping,
      s2ClaimName,
      comparisonType,
      propertyValue,
      organizationRole,
      isSuperAdmin,
      userAuthenticatedApiCall,
      identityProviderId,
      effectiveRouteS2ClaimName,
      effectiveRoutePropertyValue,
      effectiveRouteComparisonType,
      navigate,
    ],
  );

  const createClaimMapping = useAsyncCallback(
    async (isAlive) => {
      if (
        !isAlive() ||
        !isNewClaimMapping ||
        isLoadingClaimMapping ||
        isCreatingClaimMapping
      ) {
        return;
      }

      try {
        setIsCreatingClaimMapping(true);

        const requestClaimMapping: SSORoleMapping = {
          s2ClaimName,
          comparisonType,
          propertyValue,
          organizationRole: organizationRole ?? undefined,
          isSuperAdmin,
        };

        const createResult = await userAuthenticatedApiCall({
          responseSchema: SSORoleMapping,
          url: `${getApiBaseUrl()}/admin/sso/identity-providers/${identityProviderId}/role-mappings`,
          requestOptions: {
            method: 'POST',
            body: JSON.stringify(requestClaimMapping),
          },
        });

        setIsCreatingClaimMapping(false);

        if (!isAlive() || !createResult) {
          return;
        }

        if (createResult.resultType === 'err') {
          // TODO: FIXME
          setMostRecentError(JSON.stringify(createResult.error));
          return;
        }

        navigate(
          `/sso-main/identity-provider/${identityProviderId}/role-mappings`,
        );
      } catch (e) {
        setIsCreatingClaimMapping(false);
      } finally {
        setIsCreatingClaimMapping(false);
      }
    },
    [
      isNewClaimMapping,
      isLoadingClaimMapping,
      isCreatingClaimMapping,
      s2ClaimName,
      comparisonType,
      propertyValue,
      organizationRole,
      isSuperAdmin,
      userAuthenticatedApiCall,
      identityProviderId,
      navigate,
    ],
  );

  const isSaveDisabled = useMemo(() => {
    if (
      isLoadingClaimMapping ||
      !s2ClaimName ||
      s2ClaimName.length <= 0 ||
      !propertyValue ||
      propertyValue.length <= 0 ||
      !comparisonType ||
      comparisonType.length <= 0
    ) {
      return true;
    }

    if (!isSuperAdmin && !organizationRole) {
      return true;
    }

    return false;
  }, [
    isLoadingClaimMapping,
    s2ClaimName,
    propertyValue,
    comparisonType,
    isSuperAdmin,
    organizationRole,
  ]);

  const children = useMemo(() => {
    if (
      isLoadingClaimMapping ||
      isUpdatingClaimMapping ||
      isCreatingClaimMapping
    ) {
      return <p>Loading...</p>;
    }

    const welcomeMessage = isNewClaimMapping
      ? 'Create a new SSO mapping.'
      : `You are managing an SSO mapping`;

    return (
      <>
        <p>{welcomeMessage}</p>

        {mostRecentError && (
          <div
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-2"
            role="alert"
          >
            <strong className="font-bold">Error</strong>
            <span className="block pl-2">{mostRecentError}</span>
          </div>
        )}

        <div className="flex flex-row mb-2">
          <div className="basis-1/2 mr-5">
            <label className="block mb-1">
              S2 Claim Name:{' '}
              <TextInput
                id="claim-name"
                name="claim-name"
                data-qa="claimNameInput"
                value={s2ClaimName}
                type="text"
                onChange={(e) => setS2ClaimName(e.currentTarget.value)}
              />
            </label>
          </div>
        </div>

        <div className="flex flex-row mb-2">
          <div className="basis-1/2">
            <label className="block mb-1">
              Comparison:{' '}
              <select
                value={comparisonType}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  const comparisonType =
                    selectedValue === ''
                      ? null
                      : SSORoleMappingComparisonType.parse(selectedValue);
                  setComparisonType(comparisonType ?? 'equals');
                }}
                defaultValue={organizationRole === null ? '' : organizationRole}
                className="form-select border-1 border-tertiary focus:border-tertiary focus:ring-offset-2 focus:ring-offset-blue-1000 focus:ring-blue-300 focus:ring-1 font-sans mt-1 px-3 py-3 rounded-lg text-base text-gray-1000 w-full transition"
              >
                <>
                  <option key={'none'} value={''}>
                    Select a comparison technique...
                  </option>
                  {['equals', 'contains'].map((comparisonType) => {
                    return (
                      <option key={comparisonType} value={comparisonType}>
                        {comparisonType}
                      </option>
                    );
                  })}
                </>
              </select>
            </label>
          </div>
        </div>

        <div className="flex flex-row mb-2">
          <div className="basis-1/2 mr-5">
            <label className="block mb-1">
              Property Value:{' '}
              <TextInput
                id="claim-value"
                name="claim-value"
                data-qa="claimValueInput"
                value={propertyValue}
                type="text"
                onChange={(e) => setPropertyValue(e.currentTarget.value)}
              />
            </label>
          </div>
        </div>

        <div className="flex flex-row">
          <div className="basis-1/4">
            <label className="block mb-1">
              Is Super Admin?:{' '}
              <Checkbox
                checked={!!isSuperAdmin}
                onChange={(e) => setIsSuperAdmin(!!e.target.checked)}
              />
            </label>
          </div>
        </div>

        {!isSuperAdmin && (
          <>
            <div className="flex flex-row mb-2">
              <div className="basis-1/2">
                <label className="block mb-1">
                  Organization Role:{' '}
                  <select
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      const newRole =
                        selectedValue === ''
                          ? null
                          : OrganizationUserRole.parse(selectedValue);
                      setOrganizationRole(newRole);
                    }}
                    defaultValue={
                      organizationRole === null ? '' : organizationRole
                    }
                    className="form-select border-1 border-tertiary focus:border-tertiary focus:ring-offset-2 focus:ring-offset-blue-1000 focus:ring-blue-300 focus:ring-1 font-sans mt-1 px-3 py-3 rounded-lg text-base text-gray-1000 w-full transition"
                  >
                    <>
                      <option key={'none'} value={''}>
                        Select a role...
                      </option>
                      {mapOverEnum(OrganizationUserRole, (role) => {
                        return (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        );
                      })}
                    </>
                  </select>
                </label>
              </div>
            </div>
          </>
        )}

        <div>
          <LegacyButton
            variant={LegacyButtonVariant.PRIMARY_LIGHT}
            disabled={isSaveDisabled}
            onClick={
              isNewClaimMapping ? createClaimMapping : updateClaimMapping
            }
          >
            Save
          </LegacyButton>
        </div>
      </>
    );
  }, [
    isLoadingClaimMapping,
    isUpdatingClaimMapping,
    isCreatingClaimMapping,
    isNewClaimMapping,
    mostRecentError,
    s2ClaimName,
    propertyValue,
    isSuperAdmin,
    organizationRole,
    isSaveDisabled,
    createClaimMapping,
    updateClaimMapping,
    comparisonType,
  ]);

  return (
    <AdminLayout currentPage={'AdminSSORoleMapping'}>{children}</AdminLayout>
  );
};
